import React from "react";
import { Box, Typography } from "@mui/material";
import { useAppSelector } from "../../../Redux/hooks";

export type StoreOrdersTab = "new" | "preparing" | "ready" | "delivered";

interface OrderPickupDeliveryCodesProps {
  /** Which store orders tab this card belongs to (for callers; same rules apply on all tabs). */
  tab: StoreOrdersTab;
  pickupCode?: string | null;
  deliveryCode?: string | null;
}

/**
 * Super Admin (role 1): pickup + delivery codes when present.
 * Store Admin (role 3): pickup code only when present (delivery hidden).
 */
export function OrderPickupDeliveryCodes({
  tab: _tab,
  pickupCode,
  deliveryCode,
}: OrderPickupDeliveryCodesProps) {
  const roleIDRaw = useAppSelector((state: any) => state.roleAndPermission?.roleID);
  const roleID = Number(roleIDRaw);

  const pickup =
    pickupCode != null && String(pickupCode).trim() !== ""
      ? String(pickupCode).trim()
      : null;
  const delivery =
    deliveryCode != null && String(deliveryCode).trim() !== ""
      ? String(deliveryCode).trim()
      : null;

  const isSuperAdmin = roleID === 1;
  const showPickup = Boolean(pickup);
  const showDelivery = isSuperAdmin && Boolean(delivery);

  if (!showPickup && !showDelivery) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        flexDirection: "row",
        gap: 1,
        alignItems: "stretch",
        flexWrap: "wrap",
      }}
    >
      {showPickup && (
        <Box
          sx={{
            bgcolor: "#E3F2FD",
            border: "1px solid #90CAF9",
            borderRadius: 1,
            p: 1.5,
            textAlign: "center",
            flex: isSuperAdmin ? "1 1 0" : "1 1 100%",
            minWidth: 160,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#1565C0",
              fontWeight: 600,
              letterSpacing: 0.5,
              display: "block",
              mb: 0.5,
            }}
          >
            PICKUP CODE
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#1565C0", lineHeight: 1.2 }}>
            {pickup}
          </Typography>
        </Box>
      )}
      {showDelivery && (
        <Box
          sx={{
            bgcolor: "#E8F5E9",
            border: "1px solid #81C784",
            borderRadius: 1,
            p: 1.5,
            textAlign: "center",
            flex: "1 1 0",
            minWidth: 160,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#2E7D32",
              fontWeight: 600,
              letterSpacing: 0.5,
              display: "block",
              mb: 0.5,
            }}
          >
            DELIVERY CODE
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#2E7D32", lineHeight: 1.2 }}>
            {delivery}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function readCodesFromRecord(obj: Record<string, unknown> | null | undefined): {
  pickup: string | null;
  delivery: string | null;
} {
  if (!obj) return { pickup: null, delivery: null };
  const pickup =
    (obj.pickup_code as string | undefined) ??
    (obj.pickupCode as string | undefined) ??
    null;
  const delivery =
    (obj.delivery_code as string | undefined) ??
    (obj.deliveryCode as string | undefined) ??
    null;
  return {
    pickup: pickup != null && String(pickup).trim() !== "" ? String(pickup).trim() : null,
    delivery: delivery != null && String(delivery).trim() !== "" ? String(delivery).trim() : null,
  };
}

/** Resolve codes from invoice/order payloads (snake_case or camelCase), including nested `invoice` / `invoices[0]`. */
export function resolvePickupDeliveryCodes(source: unknown): {
  pickupCode: string | null;
  deliveryCode: string | null;
} {
  if (!source || typeof source !== "object") {
    return { pickupCode: null, deliveryCode: null };
  }
  const s = source as Record<string, unknown>;
  const direct = readCodesFromRecord(s);
  const invoiceObj = s.invoice as Record<string, unknown> | undefined;
  const fromInvoice = readCodesFromRecord(invoiceObj);
  const firstInv = Array.isArray(s.invoices)
    ? (s.invoices[0] as Record<string, unknown> | undefined)
    : undefined;
  const fromNestedFirst = readCodesFromRecord(firstInv);

  const pickup = direct.pickup ?? fromInvoice.pickup ?? fromNestedFirst.pickup;
  const delivery = direct.delivery ?? fromInvoice.delivery ?? fromNestedFirst.delivery;

  return {
    pickupCode: pickup,
    deliveryCode: delivery,
  };
}

